<script>
import { evaluateSLAStatus } from '@chatwoot/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { staleInfo } from './helpers/staleDeal';

export default {
  name: 'KanbanCard',
  props: {
    conversation: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    // Prazo de destaque da coluna, em horas (null = não destaca).
    staleAfterHours: {
      type: Number,
      default: null,
    },
  },
  emits: ['open-drawer', 'open-conversation'],
  computed: {
    contactName() {
      return this.conversation.meta?.sender?.name || 'Contact Unknown';
    },
    dealTitle() {
      return this.conversation.custom_attributes?.deal_title || '';
    },
    cardTitle() {
      return this.dealTitle || this.contactName;
    },
    contactInitials() {
      const name = this.contactName;
      if (!name) return '?';
      const parts = name.split(' ');
      return parts.length > 1
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },

    formattedTime() {
      try {
        const ts =
          this.conversation.last_activity_at || this.conversation.created_at;
        if (!ts) return '';
        return formatDistanceToNow(new Date(ts * 1000), {
          addSuffix: true,
          locale: ptBR,
        });
      } catch {
        return '';
      }
    },
    assigneeName() {
      return this.conversation.meta?.assignee?.name || '';
    },
    dealValue() {
      return this.conversation.custom_attributes?.deal_value;
    },
    formattedDealValue() {
      const val = parseFloat(this.dealValue);
      if (isNaN(val)) return '';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(val);
    },
    dealStatus() {
      return this.conversation.custom_attributes?.deal_status || null;
    },
    leadSourcePlatform() {
      return this.conversation.custom_attributes?.lead_source_platform || null;
    },
    // Só apaga card de CONVERSA resolvida, onde "resolvido" quer dizer
    // atendimento encerrado. Em modo deals o dealToCard traduz todo negócio não
    // aberto para 'resolved', e aí ganho e perdido — que são desfecho, não
    // abandono — ficavam apagados no quadro. O selo 🏆/❌ já diz o estado.
    isResolved() {
      return (
        !this.conversation.is_deal && this.conversation.status === 'resolved'
      );
    },
    slaStatus() {
      return evaluateSLAStatus({
        appliedSla: this.conversation.applied_sla,
        chat: this.conversation,
      });
    },
    stale() {
      return staleInfo(this.conversation, this.staleAfterHours);
    },
    cardBorderClass() {
      // Parado além do prazo da coluna vence o SLA na cor: o SLA já tem o
      // próprio aviso na conversa, e o parado é o que o gestor quer ver de
      // longe no quadro.
      if (this.stale) {
        return 'border-l-4 border-l-n-amber-9 bg-n-amber-2 border-y-n-amber-6 border-r-n-amber-6 hover:border-r-n-amber-9 hover:border-y-n-amber-9';
      }
      if (!this.conversation.sla_policy_id) {
        return 'border-n-weak hover:border-n-brand';
      }
      return this.slaStatus.isSlaMissed
        ? 'border-l-4 border-l-n-ruby-11 border-y-n-weak border-r-n-weak hover:border-r-n-brand hover:border-y-n-brand'
        : 'border-l-4 border-l-n-blue-11 border-y-n-weak border-r-n-weak hover:border-r-n-brand hover:border-y-n-brand';
    },
    tasks() {
      return this.conversation.custom_attributes?.crm_tasks || [];
    },
    // Modo deals manda contador pronto; conversational manda a lista inteira
    // dentro de custom_attributes.crm_tasks.
    pendingTasksCount() {
      const fromServer = this.conversation.custom_attributes?.crm_pending_tasks;
      if (fromServer !== undefined && fromServer !== null) return fromServer;

      return this.tasks.filter(t => !t.done).length;
    },
    // Só existe no modo deals: anexo de negócio não tem par no conversacional.
    documentsCount() {
      return this.conversation.custom_attributes?.crm_documents_count || 0;
    },
    overdueTasksCount() {
      const fromServer = this.conversation.custom_attributes?.crm_overdue_tasks;
      if (fromServer !== undefined && fromServer !== null) return fromServer;

      return this.tasks.filter(
        t => !t.done && t.dueDate && new Date(t.dueDate) < new Date()
      ).length;
    },
  },
};
</script>

<template>
  <div
    class="relative p-3 bg-n-surface-1 border rounded-lg shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-all ease-in-out"
    :class="[cardBorderClass, { 'opacity-50': isResolved }]"
    @click.stop="$emit('open-drawer', conversation)"
  >
    <div class="flex items-start justify-between mb-2">
      <!-- Contact Avatar & Name -->
      <div class="flex items-center gap-2 overflow-hidden">
        <div
          class="flex-shrink-0 w-8 h-8 rounded-full bg-n-brand/10 text-n-brand flex items-center justify-center font-semibold text-xs border border-n-brand/20"
        >
          {{ contactInitials }}
        </div>
        <div class="min-w-0">
          <h4 class="text-sm font-semibold text-n-slate-12 truncate m-0">
            {{ cardTitle }}
          </h4>
          <span class="text-xs text-n-slate-11 truncate block">
            <template v-if="dealTitle">{{ contactName }} · </template>#{{
              conversation.id
            }}
          </span>
        </div>
      </div>
      <!-- Open Conversation Shortcut -->
      <button
        class="flex-shrink-0 p-1.5 text-n-slate-10 hover:text-n-brand hover:bg-n-brand/10 rounded-md transition-colors border-none bg-transparent cursor-pointer"
        title="Abrir Conversa"
        @click.stop="$emit('open-conversation', conversation)"
      >
        <span class="i-lucide-external-link w-4 h-4" />
      </button>
    </div>

    <!-- Deal Value + Status + Origin -->
    <div
      v-if="dealValue || dealStatus || leadSourcePlatform"
      class="mt-2 mb-2 flex items-center gap-2 flex-wrap"
    >
      <span
        v-if="dealValue"
        class="text-xs font-bold text-n-blue-11 bg-n-blue-2 rounded px-2 py-1"
      >
        {{ formattedDealValue }}
      </span>
      <span
        v-if="dealStatus === 'won'"
        class="text-xs font-bold text-white bg-woot-500 rounded px-2 py-1"
      >
        🏆 Ganho
      </span>
      <span
        v-if="dealStatus === 'lost'"
        class="text-xs font-bold text-n-ruby-11 bg-n-ruby-3 rounded px-2 py-1"
      >
        ❌ Perdido
      </span>
      <span
        v-if="leadSourcePlatform"
        class="text-xs font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/40 rounded px-2 py-1 truncate max-w-[120px]"
        :title="leadSourcePlatform"
      >
        📣 {{ leadSourcePlatform }}
      </span>
    </div>

    <!-- Parado além do prazo da coluna -->
    <div v-if="stale" class="flex items-center gap-1 mb-2">
      <span
        class="text-xs font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 bg-n-amber-4 text-n-amber-12"
      >
        <span class="i-lucide-alarm-clock w-3 h-3" />
        {{ stale.label }}
      </span>
    </div>

    <!-- Task Indicators -->
    <div v-if="pendingTasksCount > 0" class="flex items-center gap-2 mb-2">
      <span
        class="text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1"
        :class="
          overdueTasksCount > 0
            ? 'bg-red-500/20 text-red-400'
            : 'bg-amber-500/20 text-amber-400'
        "
      >
        <span v-if="overdueTasksCount > 0">🔴</span>
        <span v-else>🔔</span>
        {{ pendingTasksCount }} atividade{{ pendingTasksCount > 1 ? 's' : '' }}
      </span>
    </div>

    <!-- Footer meta: timestamp and assignee -->
    <div class="flex items-center justify-between text-xs text-n-slate-10">
      <div class="flex items-center gap-2">
        <div class="flex items-center gap-1">
          <span class="i-lucide-clock w-3 h-3 text-n-slate-10" />
          <span>{{ formattedTime }}</span>
        </div>
        <div
          v-if="documentsCount > 0"
          v-tooltip.top="
            documentsCount > 1 ? `${documentsCount} anexos` : '1 anexo'
          "
          class="flex items-center gap-0.5"
        >
          <span class="i-lucide-paperclip w-3 h-3" />
          <span v-if="documentsCount > 1">{{ documentsCount }}</span>
        </div>
      </div>
      <div v-if="assigneeName" class="flex items-center gap-1">
        <span class="i-lucide-user w-3 h-3" />
        <span class="truncate max-w-[80px]">{{ assigneeName }}</span>
      </div>
    </div>
  </div>
</template>
